import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import {
  WebViewResponse,
  OrganizedObject,
  CorelObject,
  ApiOrganizedObject
} from '../lib/corel/types/messages';
import {
  setupCorelListener,
  saveOrganizedObjects,
  normalizeOrganizedObjects
} from '../lib/corelOperations';
import { logger } from '../lib/terminalLogger';
import {
  isOrganizeLayerResponse,
  isNameObjectsResponse,
  isOpenFileResponse,
  isGenericSuccessResponse,
  isMonitorCommandResponse,
  isAppStatusResponse,
  type OrganizeLayerResponse,
  type NameObjectsResponse,
  type OpenFileResponse
} from '../lib/corel/types/responses';
import { launcherReadiness } from '../lib/corel/communication/launcherReadiness';

// Tipos de respostas específicas
interface LayerOrganizerResponse {
  sucesso: boolean;
  mensagem: string;
  total: number;
  objetos: ApiOrganizedObject[]; // Formato recebido da API
}

interface NomeadorResponse {
  sucesso: boolean;
  mensagem: string;
  total: number;
  objetos: CorelObject[];
}

// Tipos de estado que o contexto vai armazenar
interface CorelState {
  isConnected: boolean;
  lastResponse: WebViewResponse | null;
  lastError: string | null;
  organizedObjects: OrganizedObject[] | null;
  namedObjects: CorelObject[] | null;
  isBusy: boolean;
}

// Tipo das funções que o contexto vai expor
interface CorelContextType extends CorelState {
  processResponse: (response: WebViewResponse) => void;
  handleError: (error: Error, data: any) => void;
  clearData: () => void;
  hasOrganizedObjects: boolean;
  hasNamedObjects: boolean;
}

// Criação do contexto
const CorelContext = createContext<CorelContextType | undefined>(undefined);

// Props para o provider
interface CorelProviderProps {
  children: ReactNode;
  onShowModal?: (title: string, content: React.ReactNode, size?: 'sm' | 'md' | 'lg') => void;
}

// Provider Component
/**
 * Provider para estado e interações com CorelDRAW via WebView2.
 * Gerencia respostas, erros e dados como objetos organizados/nomeados.
 */
export const CorelProvider: React.FC<CorelProviderProps> = ({ children, onShowModal }) => {
  // Estado para armazenar dados relacionados ao Corel
  const [corelState, setCorelState] = useState<CorelState>({
    isConnected: false,
    lastResponse: null,
    lastError: null,
    organizedObjects: null,
    namedObjects: null,
    isBusy: false
  });

  // Processa as respostas recebidas do Corel
  const processResponse = (response: WebViewResponse) => {
    logger.debug('🔍 RequestId:', response.requestId);
    logger.debug('📊 Status:', response.status);
    logger.debug('📊 Result:', response.result);
    
    setCorelState(prevState => ({ 
      ...prevState, 
      lastResponse: response,
      lastError: null,
      isConnected: true,
      isBusy: false
    }));

    // Processa de acordo com o tipo de resposta
    if (response.status === 'ok' && response.result) {
      logger.debug('✅ Resposta com status OK, processando resultado');
      
      // Resposta da organização de camadas
      if (isOrganizeLayerResponse(response)) {
        logger.debug('📊 Processando resposta de organização de camadas');
        const organizerResponse = response as OrganizeLayerResponse;
        
        // Normaliza os objetos do formato da API para o formato da aplicação
        const normalizedObjects = normalizeOrganizedObjects(organizerResponse.result.objetos);
        logger.debug('🔄 Objetos normalizados:', normalizedObjects);
        
        // Salva os objetos normalizados no localStorage
        const saved = saveOrganizedObjects(normalizedObjects);
        logger.debug('💾 Dados salvos com sucesso?', saved ? 'Sim' : 'Não');
        
        setCorelState(prevState => ({ 
          ...prevState,
          organizedObjects: normalizedObjects
        }));
      } 
      // Resposta do nomeador de objetos
      else if (isNameObjectsResponse(response)) {
        logger.debug('🏷️ Processando resposta de nomeação de objetos');
        const nomeadorResponse = response as NameObjectsResponse;
        
        setCorelState(prevState => ({ 
          ...prevState,
          namedObjects: nomeadorResponse.result.objetos
        }));
      } 
      // Resposta de abrirArquivo
      else if (isOpenFileResponse(response)) {
        logger.debug('📁 Processando resposta de abrirArquivo');
        const openFileResponse = response as OpenFileResponse;
        logger.debug('✅ Arquivo aberto com sucesso:', openFileResponse.result?.sucesso);
        // Não precisamos fazer nada específico aqui, apenas log
      }
      // Resposta genérica de sucesso (sem dados específicos)
      else if (isGenericSuccessResponse(response)) {
        logger.debug('✅ Resposta genérica de sucesso (sem dados específicos)');
        // Não precisa fazer nada, apenas aceitar
      }
      // Resposta de monitoramento (não processada aqui, mas reconhecida)
      else if (isMonitorCommandResponse(response)) {
        logger.debug('📊 Resposta de monitoramento (processada pelo MonitorResponseAdapter)');
        // Monitoramento é processado pelo MonitorResponseAdapter, não aqui
      }
      // Resposta de app-status (não processada aqui, mas reconhecida)
      else if (isAppStatusResponse(response)) {
        logger.debug('📊 Resposta de app-status (processada pelo useCorelConnection)');
        // app-status é processado pelo useCorelConnection, não aqui
      }
      // Formato não reconhecido (mas não crítico)
      else {
        // Log mais detalhado para debug, mas não como erro crítico
        logger.debug('⚠️ Formato de resposta não reconhecido (mas não crítico):', {
          requestId: response.requestId,
          resultKeys: Object.keys(response.result || {}),
          resultType: typeof response.result,
          resultPreview: JSON.stringify(response.result).substring(0, 200)
        });
      }
    } else if (response.status === 'erro') {
      const msg = response.message ?? 'sem message';
      const action = (response as any).action ?? 'sem action';
      
      // ✅ Silenciar erros de actions não implementadas no C# (fire-and-forget)
      const SILENCED_ACTIONS = ['notificar-webview-disponivel'];
      if (SILENCED_ACTIONS.includes(action)) {
        // Não logar - action não implementada no C# mas não é crítica
        return;
      }
      
      // 🔍 DEBUG: Log inline com todos os detalhes para diagnóstico
      const requestId = (response as any).requestId ?? 'sem requestId';
      const resultStr = JSON.stringify(response.result ?? {}).substring(0, 200);
      
      // ✅ DEBUG: Log especial para launcher não pronto (causa do "Algo inesperado")
      const isLauncherNotReady = msg.includes('launcher_session_required') || 
                                 msg.includes('booting') ||
                                 resultStr.includes('"launcherState":"booting"');
      
      if (isLauncherNotReady) {
        logger.error(`🚨 [COREL_ERROR] LAUNCHER NÃO PRONTO - action=${action} | msg=${msg} | reqId=${requestId} | result=${resultStr}`);
        sendToTerminal(`[COREL_CONTEXT] Erro: Launcher não está pronto`, {
          action,
          message: msg,
          result: response.result,
          suggestion: 'Aguardar inicialização completa do Launcher'
        });
        
        // ✅ NOVO: Atualizar estado do launcher para trigger de retry automático
        launcherReadiness.updateStateFromResponse(response);
        
        // ✅ NOVO: Se a action é crítica, logar apenas (o retry já está sendo gerenciado pelo LauncherReadinessManager)
        const criticalActions = ['lock-document', 'shortcuts:disable', 'shortcuts:enable', 'plotcalc:set-api-config'];
        if (criticalActions.includes(action)) {
          sendToTerminal('🔄 [COREL_CONTEXT] Action crítica detectada, retry automático agendado', {
            action,
            requestId,
            queueSize: launcherReadiness.getStats().queueSize
          });
        }
      } else {
        // Log com dados INLINE na mensagem para garantir exibição
        logger.error(`❌ [COREL_ERROR] action=${action} | msg=${msg} | reqId=${requestId} | result=${resultStr}`);
      }
      
      setCorelState(prevState => ({ 
        ...prevState,
        lastError: response.message || 'Erro desconhecido'
      }));
    }
  };

  // Trata erros
  const handleError = (error: Error, data: any) => {
    setCorelState(prevState => ({ 
      ...prevState,
      lastError: error.message,
      isBusy: false
    }));
  };

  // Limpa os dados do estado
  const clearData = () => {
    setCorelState(prevState => ({ 
      ...prevState,
      lastResponse: null,
      lastError: null,
      organizedObjects: null,
      namedObjects: null
    }));
  };

  // Configura o listener de eventos do WebView quando o componente é montado
  useEffect(() => {
    const cleanupListener = setupCorelListener(processResponse, handleError);
    
    // Limpa o listener quando o componente é desmontado
    return () => {
      cleanupListener();
    };
  }, []);

  // Valor do contexto — memoizado para evitar re-renders desnecessários nos consumidores
  const contextValue = useMemo<CorelContextType>(() => ({
    ...corelState,
    processResponse,
    handleError,
    clearData,
    hasOrganizedObjects: corelState.organizedObjects !== null && corelState.organizedObjects.length > 0,
    hasNamedObjects: corelState.namedObjects !== null && corelState.namedObjects.length > 0
  }), [corelState, processResponse, handleError, clearData]);

  return (
    <CorelContext.Provider value={contextValue}>
      {children}
    </CorelContext.Provider>
  );
};

/**
 * Hook para acessar o contexto do Corel.
 * Garante uso dentro de um CorelProvider.
 */
export const useCorel = (): CorelContextType => {
  const context = useContext(CorelContext);
  
  if (context === undefined) {
    throw new Error('useCorel deve ser usado dentro de um CorelProvider');
  }
  
  return context;
};